// "Possibile duplicato" from the WhatsApp profile name (owner, 2026-10-21): the person
// writes from another number and the profile name is the email or the name of another
// lead. A warning only, never a merge: the operator decides with "Verifica e unisci".
// Instagram and Messenger (ADR 0092) give no number: the same person on two channels is
// two leads, and the same name and surname is the hint ("È la stessa persona?").
import type { LeadSource } from "./domain";

/** Leads born further apart are not compared: the same name months later is someone else. */
export const NAME_SIGNAL_WINDOW_DAYS = 21;

/**
 * Very common Italian first names and surnames: "Marco Rossi" does not tell two
 * people apart, "Alessio Boro" does.
 */
const COMMON_NAMES = new Set(
  [
    "alberto alessandra alessandro alessio alice andrea angela angelo anna antonella antonio",
    "aurora barbara beatrice carla carlo chiara christian claudio cristian cristina daniela",
    "daniele davide diego domenico edoardo elena elisa emanuele emma enrico fabio federica",
    "federico filippo francesca francesco franco gabriele gianluca giorgia giorgio giovanna",
    "giovanni giulia giuseppe ilaria laura leonardo lorenzo luca lucia luigi manuela marco",
    "maria mario marta martina massimo matteo mattia michele monica nicola paola paolo",
    "patrizia pietro raffaele riccardo roberta roberto rosa salvatore sara serena sergio",
    "silvia simona simone sofia stefania stefano teresa tommaso valentina vincenzo",
    "amato barbieri barone basile bellini benedetti bernardi bianchi bianco bruno caputo",
    "carbone caruso cattaneo colombo coppola conte conti costa damico deluca derosa esposito",
    "fabbri farina ferrara ferrari ferraro ferri fiore fontana galli gallo gatti gentile",
    "giordano grassi grasso greco guerra leone lombardi lombardo longo mancini marchetti",
    "mariani marini marino martinelli martini mazza messina monti morelli moretti neri",
    "orlando palmieri palumbo parisi pellegrini pellegrino rinaldi ricci rizzi rizzo romano",
    "rossi russo sala santoro serra silvestri testa valentini villa vitale",
  ]
    .join(" ")
    .split(" "),
);

export type NameSignalLead = {
  source: LeadSource;
  fullName: string | null;
  email: string | null;
  firstSeenAt: Date;
};

export type NameSignal =
  | {
      signal: "whatsapp_name";
      /** What of the other lead the WhatsApp name matches. */
      matched: "email" | "name";
      whatsappName: string;
    }
  | {
      /** A name on Instagram or Messenger, or a WhatsApp name against one of theirs. */
      signal: "profile_name";
      matched: "email" | "name";
      profileName: string;
    };

/** Leads named by the contact's own profile on a chat app. */
const PROFILE_SOURCES: ReadonlySet<LeadSource> = new Set(["whatsapp", "instagram", "messenger"]);
const isMessagingSource = (source: LeadSource) => source === "instagram" || source === "messenger";

/** Words without accents, case or punctuation: "Alessio2011.Boro" → alessio2011, boro. */
function words(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/u)
    .filter(Boolean);
}

/** A word that tells people apart: with digits, or long enough and not a common name. */
function distinctive(word: string): boolean {
  if (/\d/u.test(word)) return word.length >= 4;
  return word.length >= 4 && !COMMON_NAMES.has(word);
}

function signalFrom(
  chat: NameSignalLead,
  other: NameSignalLead,
): Extract<NameSignal, { signal: "whatsapp_name" }> | null {
  const name = words(chat.fullName);
  // One word ("Marco", "Studio") is never enough.
  if (!chat.fullName || name.length < 2) return null;
  const local = words(other.email?.split("@")[0]);
  const sameWords = [...name].sort().join(" ") === [...local].sort().join(" ");
  if (
    local.length > 0 &&
    local.join("").length >= 8 &&
    (name.join("") === local.join("") || sameWords) &&
    local.some(distinctive)
  )
    return { signal: "whatsapp_name", matched: "email", whatsappName: chat.fullName };
  // Name and surname, in any order; "alessio2011" counts as "alessio".
  const letters = (word: string) => word.replace(/\d+/gu, "");
  const chatWords = name.map(letters).filter((word) => word.length >= 3);
  const otherWords = new Set(
    words(other.fullName)
      .map(letters)
      .filter((word) => word.length >= 3),
  );
  if (
    chatWords.length >= 2 &&
    chatWords.every((word) => otherWords.has(word)) &&
    chatWords.some(distinctive)
  )
    return { signal: "whatsapp_name", matched: "name", whatsappName: chat.fullName };
  return null;
}

const bornApart = (first: NameSignalLead, second: NameSignalLead) =>
  Math.abs(first.firstSeenAt.getTime() - second.firstSeenAt.getTime()) >
  NAME_SIGNAL_WINDOW_DAYS * 24 * 60 * 60 * 1_000;

/**
 * The weak signal between two leads: the WhatsApp profile name of one is the
 * email (before the @) or the name and surname of the other, both born within
 * a few weeks. Null without it.
 */
export function whatsappNameSignal(
  first: NameSignalLead,
  second: NameSignalLead,
): Extract<NameSignal, { signal: "whatsapp_name" }> | null {
  if (bornApart(first, second)) return null;
  if (first.source === "whatsapp") {
    const signal = signalFrom(first, second);
    if (signal) return signal;
  }
  return second.source === "whatsapp" ? signalFrom(second, first) : null;
}

/**
 * The weak signal of "Possibile duplicato", for any pair of leads: with an
 * Instagram or Messenger lead, its profile name (or the WhatsApp name of the
 * other) against the other's name or email, by the same rules as WhatsApp's.
 * Two leads of the same Instagram or Messenger are two accounts: never a hint.
 * Without them, the WhatsApp name (`whatsappNameSignal`).
 */
export function leadNameSignal(first: NameSignalLead, second: NameSignalLead): NameSignal | null {
  if (!isMessagingSource(first.source) && !isMessagingSource(second.source))
    return whatsappNameSignal(first, second);
  if (first.source === second.source || bornApart(first, second)) return null;
  for (const [chat, other] of [
    [first, second],
    [second, first],
  ] as const) {
    if (!PROFILE_SOURCES.has(chat.source)) continue;
    const match = signalFrom(chat, other);
    if (match)
      return { signal: "profile_name", matched: match.matched, profileName: match.whatsappName };
  }
  return null;
}
